import { Calculate } from "@mui/icons-material";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Typography } from "@mui/material";
import React, { useState, useCallback } from "react";
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { accountApi, useFindAccountByPeriodQuery } from "../../../../services/accountService/account-api";
import { AccountSummaryRequest } from "../../../../services/accountService/commands";
import {
    loadAccounts,
    resetCalculation,
    setPhase,
    calculateBatchReimbursement
} from "../../../../services/accountService/model/account/ReimbursementCalcSlice";
import { ReimbursementCalcGrid } from "./ReimbursementCalcGrid";

function ReimbursementCalcDialog() {
    const dispatch = useAppDispatch();
    const { center, month, year } = useParams();
    const [isOpen, setIsOpen] = useState(false);

    // Get accounts for the current period
    const { data: periodAccounts } = useFindAccountByPeriodQuery(
        new AccountSummaryRequest(center!, Number(month!), Number(year!)),
        { skip: !isOpen }
    );

    const { rows, phase, processedCount, successCount, warningCount, errorCount } = useAppSelector(state => state.reimbursementCalc);

    const isProcessing = phase === 'calculating';
    const isComplete = phase === 'complete';
    const totalRows = rows.length;
    const progress = totalRows > 0 ? (processedCount / totalRows) * 100 : 0;

    const handleOpen = () => {
        setIsOpen(true);
    };

    const handleStartCalculation = useCallback(async () => {
        if (!periodAccounts || periodAccounts.length === 0) return;

        const overallStartTime = performance.now();
        const MAX_BATCH_SIZE = 500;
        console.log(`Starting batch calculation for ${periodAccounts.length} accounts`);

        // Load accounts into the slice
        dispatch(loadAccounts({
            accounts: periodAccounts.map(acc => ({
                accountId: acc.id,
                accountNumber: acc.accountNumber
            }))
        }));

        dispatch(setPhase('calculating'));

        // Process accounts in chunks of 500 (Parsec SDK batch limit)
        const chunks: string[][] = [];
        for (let i = 0; i < periodAccounts.length; i += MAX_BATCH_SIZE) {
            chunks.push(periodAccounts.slice(i, i + MAX_BATCH_SIZE).map(a => a.id));
        }

        console.log(`Processing ${chunks.length} batch chunk(s)`);

        // Process chunks sequentially
        for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
            const chunk = chunks[chunkIndex];
            const chunkStartTime = performance.now();
            
            try {
                await dispatch(calculateBatchReimbursement({
                    accountIds: chunk
                })).unwrap();
                
                const chunkDuration = performance.now() - chunkStartTime;
                console.log(`Chunk ${chunkIndex + 1}/${chunks.length} completed in ${chunkDuration.toFixed(2)}ms`);
            } catch (error) {
                console.error(`Error processing chunk ${chunkIndex + 1}:`, error);
            }
        }

        const overallEndTime = performance.now();
        const totalDuration = overallEndTime - overallStartTime;
        console.log(`Overall batch calculation completed in ${totalDuration.toFixed(2)}ms`);

        dispatch(setPhase('complete'));
    }, [dispatch, periodAccounts]);

    const handleClose = () => {
        // Prevent closing during processing
        if (isProcessing) {
            return;
        }

        // Invalidate cache to refresh data and retrigger grid load
        dispatch(accountApi.util.invalidateTags(['AccountSummary']));

        // Reset state
        dispatch(resetCalculation());
        setIsOpen(false);
    };

    const handleDialogClose = (_event: object, reason: string) => {
        // Only allow closing via the Cancel/Close button, not backdrop or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        handleClose();
    };

    return (
        <React.Fragment>
            <Button variant='outlined' color='primary' onClick={handleOpen} startIcon={<Calculate />}>
                Calculate Reimbursement
            </Button>

            <Dialog
                open={isOpen}
                onClose={handleDialogClose}
                maxWidth='md'
                fullWidth
                disableEscapeKeyDown={true}
            >
                <DialogTitle>Calculate Reimbursement</DialogTitle>
                <DialogContent>
                    <Stack gap={2} marginTop='10px'>
                        <Alert severity='info'>
                            This will calculate expected reimbursement for all accounts in the current period using contract data.
                            {periodAccounts && (
                                <Box marginTop={1}>
                                    <strong>{periodAccounts.length}</strong> accounts will be processed.
                                </Box>
                            )}
                        </Alert>

                        {isProcessing && (
                            <Box>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Processing {processedCount} of {totalRows} accounts...
                                </Typography>
                                <LinearProgress variant="determinate" value={progress} />
                            </Box>
                        )}

                        {isComplete && (
                            <Alert severity={errorCount > 0 ? "error" : warningCount > 0 ? "warning" : "success"}>
                                Calculation complete: {successCount} succeeded{warningCount > 0 && `, ${warningCount} with warnings`}{errorCount > 0 && `, ${errorCount} failed`}
                            </Alert>
                        )}

                        {rows.length > 0 && (
                            <ReimbursementCalcGrid />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        color='secondary'
                        disabled={isProcessing}
                    >
                        {isComplete ? 'Close' : 'Cancel'}
                    </Button>

                    {phase === 'idle' && periodAccounts && periodAccounts.length > 0 && (
                        <Button
                            onClick={handleStartCalculation}
                            variant='contained'
                            color='primary'
                        >
                            Start Calculation
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export { ReimbursementCalcDialog };
